import { ensureArray } from '@agilepoint/utils-array';

interface IListIteratorOptions<T> {
    initialSelectedIndex?: number;
    loop?: boolean;
    onChange?: Subscriber<T>;
    canSelectItem?(item: T): boolean;
}

type Subscriber<T> = (value: T) => void;

export class ListIterator<T> {
    private currentIndex: number;
    private list: T[];
    private changeListeners: Array<Subscriber<T>> = [];

    constructor(list: T[], private options: IListIteratorOptions<T> = {}) {
        const { initialSelectedIndex = -1, onChange } = options;
        this.currentIndex = initialSelectedIndex;
        this.list = [...list];
        if (onChange) {
            this.changeListeners = ensureArray(onChange);
        }
    }

    updateOptions(options: Partial<IListIteratorOptions<T>>) {
        this.options = { ...this.options, ...options };
        return this;
    }

    updateList(newList: T[]) {
        this.list = newList;
        return this;
    }

    find(predicate: (item: T) => boolean) {
        const index = this.list.findIndex(predicate);
        this.currentIndex = index > -1 ? (this.canSelectItem(this.list[index]) ? Math.max(0, index) : -1) : index;
        this.onChange();
        return this;
    }

    next(itemsToSkip = 0, forcedIndex?: number) {
        const { loop } = this.options;
        let newIndex = forcedIndex !== undefined ? forcedIndex : this.currentIndex + Math.max(itemsToSkip, 1);
        const size = this.list.length;
        const lastIndex = size - 1;
        newIndex = (loop && this.currentIndex === lastIndex) ? 0 : Math.min(newIndex, lastIndex);
        if (!this.canSelectItem(this.list[newIndex])) {
            if (newIndex !== lastIndex) {
                this.next(0, newIndex + 1);
            }
            return this;
        }
        this.currentIndex = newIndex;
        this.onChange();
        return this;
    }

    prev(itemsToSkip = 0, forcedIndex?: number) {
        const { loop } = this.options;
        let newIndex = forcedIndex !== undefined ? forcedIndex : this.currentIndex - Math.max(itemsToSkip, 1);
        const size = this.list.length;
        const lastIndex = size - 1;
        newIndex = (loop && this.currentIndex === 0) ? lastIndex : Math.max(0, newIndex);
        if (!this.canSelectItem(this.list[newIndex])) {
            if (newIndex !== 0) {
                this.prev(0, newIndex - 1);
            }
            return this;
        }
        this.currentIndex = newIndex;
        this.onChange();
        return this;
    }

    reset() {
        this.currentIndex = -1;
        this.onChange();
    }

    getValue() {
        return this.list[this.currentIndex];
    }

    getAllValues() {
        return this.list;
    }

    getLength() {
        return this.list.length;
    }

    getcurrentIndex() {
        return this.currentIndex;
    }

    subscribeToOnChange(subscriber: Subscriber<T>) {
        if (!this.changeListeners.includes(subscriber)) {
            this.changeListeners.push(subscriber);
        }
        return this;
    }

    unsubscribeToOnChange(subscriber: Subscriber<T>) {
        const subscriberIndex = this.changeListeners.indexOf(subscriber);
        this.changeListeners.splice(subscriberIndex, 1);
        return this;
    }

    private onChange() {
        const activeItem = this.getValue();
        this.changeListeners.forEach(listener => listener(activeItem));
    }

    private canSelectItem(item: T) {
        const { canSelectItem } = this.options;
        return canSelectItem === undefined ? true : canSelectItem(item);
    }
}
